// File: frontend/src/components/brain/KatifiVideoArchive.js
// Description: Secret Video Archive tab in Admin Brain Center — shows all AI-generated videos

import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

const PERSONAS = [
  { value: 'all', label: '🎬 Все видео' },
  { value: 'katifi', label: '💜 Катифи' },
  { value: 'julifi', label: '⭐ Джулифи' },
  { value: 'katifi_and_julifi', label: '👯 Обе' },
];

export default function KatifiVideoArchive() {
  const { user } = useAuth();
  const isRealCreator = Boolean(user?.email && user.email.toLowerCase() === 'afitbk@gmail.com');

  const [videos, setVideos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [persona, setPersona] = useState('all');
  const [page, setPage] = useState(0);
  const [activeVideo, setActiveVideo] = useState(null);
  const limit = 12;

  const fetchVideos = useCallback(async () => {
    if (!isRealCreator) return;
    setLoading(true);
    try {
      const res = await apiService.get(
        `/admin/katifi/videos?persona=${persona}&limit=${limit}&offset=${page * limit}`
      );
      setVideos(res.data.videos || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Video archive fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [persona, page]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const totalPages = Math.ceil(total / limit);

  if (!isRealCreator) {
    return null;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.lockIcon}>🔐</span>
          <div>
            <h2 style={styles.title}>Секретный Видео Архив</h2>
            <p style={styles.subtitle}>Все AI-генерированные видео · {total} файлов</p>
          </div>
        </div>
        <button onClick={fetchVideos} style={styles.refreshBtn} disabled={loading}>
          {loading ? '⏳' : '🔄'} Обновить
        </button>
      </div>

      {/* Persona Filter */}
      <div style={styles.filterRow}>
        {PERSONAS.map(p => (
          <button
            key={p.value}
            onClick={() => { setPersona(p.value); setPage(0); }}
            style={{
              ...styles.filterBtn,
              ...(persona === p.value ? styles.filterBtnActive : {})
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Video Grid */}
      {loading ? (
        <div style={styles.loadingBox}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Загружаю архив...</p>
        </div>
      ) : videos.length === 0 ? (
        <div style={styles.emptyBox}>
          <span style={{ fontSize: 48 }}>🎬</span>
          <p style={{ color: '#94a3b8', marginTop: 12 }}>Архив пуст. Попроси Катифи снять видео!</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {videos.map(v => (
            <div key={v.id} style={styles.card} onClick={() => setActiveVideo(v)}>
              <video
                src={v.url.startsWith('http') ? v.url : `https://mlg.nygot.com${v.url}`}
                style={styles.videoThumb}
                muted
                loop
                onMouseEnter={e => e.target.play()}
                onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                preload="metadata"
              />
              <div style={styles.cardOverlay}>
                <span style={styles.personaBadge}>
                  {v.persona === 'katifi' ? '💜 Катифи' : v.persona === 'julifi' ? '⭐ Джулифи' : '👯 Обе'}
                </span>
                <div style={styles.cardPrompt}>{v.prompt}</div>
                <div style={styles.cardDate}>{v.created_at}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={styles.pageBtn}>← Назад</button>
          <span style={styles.pageInfo}>{page + 1} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={styles.pageBtn}>Вперёд →</button>
        </div>
      )}

      {/* Lightbox Modal */}
      {activeVideo && (
        <div style={styles.modal} onClick={() => setActiveVideo(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button style={styles.modalClose} onClick={() => setActiveVideo(null)}>✕</button>
            <video
              src={activeVideo.url.startsWith('http') ? activeVideo.url : `https://mlg.nygot.com${activeVideo.url}`}
              controls
              autoPlay
              style={styles.modalVideo}
            />
            <div style={styles.modalMeta}>
              <span style={styles.personaBadge}>
                {activeVideo.persona === 'katifi' ? '💜 Катифи' : activeVideo.persona === 'julifi' ? '⭐ Джулифи' : '👯 Обе'}
              </span>
              <p style={styles.modalPrompt}>{activeVideo.prompt}</p>
              <p style={styles.modalDate}>{activeVideo.created_at}</p>
              <a
                href={activeVideo.url.startsWith('http') ? activeVideo.url : `https://mlg.nygot.com${activeVideo.url}`}
                download
                style={styles.downloadBtn}
              >
                ⬇️ Скачать MP4
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '24px', background: '#0f172a', minHeight: '100vh', color: '#f8fafc' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, background: 'linear-gradient(135deg, #1e1b4b, #312e81)', borderRadius: 16, padding: '20px 24px', border: '1px solid #4c1d95' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  lockIcon: { fontSize: 36 },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#e0e7ff', background: 'linear-gradient(135deg, #a78bfa, #c084fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#94a3b8' },
  refreshBtn: { background: '#312e81', border: '1px solid #4c1d95', color: '#a78bfa', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  filterRow: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  filterBtn: { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '8px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 500, transition: 'all 0.2s' },
  filterBtnActive: { background: 'linear-gradient(135deg, #4c1d95, #6d28d9)', border: '1px solid #7c3aed', color: '#e0e7ff' },
  loadingBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 16 },
  spinner: { width: 48, height: 48, border: '4px solid #4c1d95', borderTopColor: '#a78bfa', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { color: '#94a3b8' },
  emptyBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 },
  card: { position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#1e293b', border: '1px solid #334155', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', aspectRatio: '9/16', maxHeight: 360 },
  videoThumb: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  cardOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.85))', padding: '32px 12px 12px' },
  personaBadge: { background: 'rgba(124, 58, 237, 0.8)', color: '#e0e7ff', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  cardPrompt: { color: '#cbd5e1', fontSize: 11, marginTop: 6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
  cardDate: { color: '#64748b', fontSize: 10, marginTop: 4 },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 32 },
  pageBtn: { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  pageInfo: { color: '#94a3b8', fontSize: 14 },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' },
  modalContent: { background: '#0f172a', borderRadius: 16, border: '1px solid #334155', overflow: 'hidden', maxWidth: 480, width: '90%', position: 'relative' },
  modalClose: { position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16, zIndex: 10 },
  modalVideo: { width: '100%', maxHeight: '60vh', background: '#000' },
  modalMeta: { padding: 16 },
  modalPrompt: { color: '#94a3b8', fontSize: 13, marginTop: 8 },
  modalDate: { color: '#64748b', fontSize: 11, marginTop: 4 },
  downloadBtn: { display: 'inline-block', marginTop: 12, background: 'linear-gradient(135deg, #4c1d95, #6d28d9)', color: '#e0e7ff', padding: '8px 16px', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600 },
};
